/** @jsx React.createElement */
/** @jsxFrag React.Fragment */
import * as React from 'react';
import {useEffect} from 'react';
import {Button, Typography} from 'onboarding-components';
import clsx from 'clsx';
import moment from 'moment';
import copy from 'clipboard-copy';

import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {SectionReact} from 'common/components/section';
import {SvgIconReact} from 'common/components/svg-icon';
import {TEXT_HIGHLIGHT_THEME, TextHighlightReact} from 'common/components/text-highlight';
import {Content, Header} from 'common/onboarding/immersive-onboarding/components';
import sendData from 'common/onboarding/immersive-onboarding/assets/send-data.webp';
import bq from 'common/onboarding/immersive-onboarding/assets/bq.svg';
import gtm from 'common/onboarding/immersive-onboarding/assets/googletag.svg';
import rudderstack from 'common/onboarding/immersive-onboarding/assets/rudderstack.svg';
import snowflake from 'common/onboarding/immersive-onboarding/assets/snowflake.svg';
import segment from 'common/onboarding/immersive-onboarding/assets/segment.svg';
import more from 'common/onboarding/immersive-onboarding/assets/more.svg';
import js from 'common/onboarding/immersive-onboarding/assets/JS.svg';
import android from 'common/onboarding/immersive-onboarding/assets/android.svg';
import ios from 'common/onboarding/immersive-onboarding/assets/iOS.svg';
import eventListenerGif from 'common/onboarding/immersive-onboarding/assets/events-heart-beat.gif';
import eventDetected from 'common/onboarding/immersive-onboarding/assets/events-detected.svg';
import checkmark from 'common/onboarding/immersive-onboarding/assets/checkmark.svg';
import {reporter} from 'common/report/error-reporting';
import {appApiFetchJson, appApiUrl} from 'common/report/queries/utils';
import {apiClient} from 'common/util/api-client';
import {Banner} from 'common/onboarding/immersive-onboarding/components/banner';
import {SetupGuideContext, Step} from 'common/onboarding/immersive-onboarding/types';
import type {ChromeHeader} from 'common/widgets/chrome/chrome-header';
import {VisibilityContext} from 'common/onboarding/immersive-onboarding/types';

import styles from './implement.module.styl';

type ImplementMethodProps = {
  title: string;
  url: string;
  imgSrc: string;
  highlight?: string;
  hasMarginLeft?: boolean;
};

export const Implement = () => {
  const {setStep, setupGuideInfo} = React.useContext(SetupGuideContext);
  const paths = window.location.pathname.split(`/`);
  const projectId = paths[paths.indexOf(`project`) + 1];

  const copyToken = async (token: string) => {
    try {
      await copy(token);
      addToast({
        type: ToastType.SUCCESS,
        message: `Token Copied!`,
      });
    } catch (err) {
      reporter.error(err);
      addToast({
        type: ToastType.ERROR,
        message: `Something went wrong copying the link`,
      });
    }
  };

  const ProjectToken = () => {
    const [projectToken, setProjectToken] = React.useState(null);

    const getProjectToken = async () => {
      try {
        // TODO: Zihe, please replace this with the proper token endpoint
        const res = await appApiFetchJson(`/settings/project/${projectId}/metadata`, {
          method: `get`,
        });
        setProjectToken(res.token.slice(0, 15) + `…`);
      } catch (error) {
        console.error(error);
      }
    };

    useEffect(() => {
      getProjectToken();
    }, [projectId]);

    return (
      <div className={styles.tokenContainer}>
        {`Copy your project token: `}
        <span className={styles.token}>
          <code>{projectToken}</code>
          <button
            className={styles.copyTokenButton}
            onClick={() => {
              copyToken(`${projectToken}`);
            }}
          >
            <SvgIconReact icon={`copy`} />
          </button>
        </span>
      </div>
    );
  };

  const ImplementMethod = ({title, url, imgSrc, highlight, hasMarginLeft}: ImplementMethodProps) => {
    return (
      <a
        className={clsx(styles.implementMethod, {[styles.marginLeft]: hasMarginLeft})}
        href={url}
        target='_blank'
        onClick={() => {
          // TODO: add tracking
        }}
      >
        <div className={styles.implementMethodIcon}>
          <img src={imgSrc} />
        </div>

        {highlight ? (
          <div className={styles.implementMethodTextContainer}>
            <Typography variant='captionXs' weight='bold' className={styles.implementMethodText}>
              {title}
            </Typography>
            <TextHighlightReact size='small' theme={TEXT_HIGHLIGHT_THEME.SUCCESS} text={highlight} />
          </div>
        ) : (
          <Typography variant='captionXs' weight='bold' className={styles.implementMethodText}>
            {title}
          </Typography>
        )}
      </a>
    );
  };

  const SectionWithMethods = ({children, headerTitle}) => (
    //@ts-ignore
    <SectionReact
      //@ts-ignore
      headerTitle={headerTitle}
      //@ts-ignore
      headerTitleLowercase={true}
      //@ts-ignore
      isClosed={false}
      //@ts-ignore
      styleOverride={`.mp-section-title-container {padding-left: 20px} .mp-section-title {color: var(--gray5); font-size: 16px;}`}
    >
      {children}
    </SectionReact>
  );

  const eventList = (events) => {
    if (!events || events.length === 0) {
      return ``;
    }
    if (events.length === 1) {
      return events[0];
    }
    return events.slice(0, -1).join(`, `) + ` and ` + events.slice(-1);
  };

  const EventBanner = ({events, lastEditedUser, lastEditedDate}) => {
    return (
      <>
        <Banner
          title={`${events.length} planned events: ${eventList(events)}`}
          description={`Last edited by ${lastEditedUser} on ${lastEditedDate}`}
          actionName={`Edit events`}
          action={() => {
            setStep(Step.Events);
          }}
          background={`#eae6e7`}
        />
      </>
    );
  };

  const ImplementCdpCTA = () => {
    return (
      <>
        <div className={styles.implementSection}>
          <SectionWithMethods headerTitle='I already collect data with a CDP or Data Warehouse'>
            <div className={styles.implementMethods}>
              <div className={styles.implementMethodRow}>
                <ImplementMethod
                  title={`Segment`}
                  url={`https://developer.mixpanel.com/docs/segment`}
                  imgSrc={segment}
                />
                <ImplementMethod
                  title={`RudderStack`}
                  url={`https://developer.mixpanel.com/docs/rudderstack`}
                  imgSrc={rudderstack}
                  hasMarginLeft={true}
                />
                <ImplementMethod
                  title={`Google Tag Manager`}
                  url={`https://docs.mixpanel.com/docs/tracking/integrations/google-tag-manager`}
                  imgSrc={gtm}
                  hasMarginLeft={true}
                />
              </div>
              <div className={styles.implementMethodRow}>
                <ImplementMethod
                  title={`Snowflake`}
                  url={`https://docs.mixpanel.com/docs/tracking-methods/data-warehouse/snowflake`}
                  imgSrc={snowflake}
                  highlight={`Codeless`}
                />
                <ImplementMethod
                  title={`BigQuery`}
                  url={`https://docs.mixpanel.com/docs/tracking/data-warehouse/bigquery`}
                  imgSrc={bq}
                  highlight={`Codeless`}
                  hasMarginLeft={true}
                />
                <ImplementMethod
                  title={`More Options`}
                  url={`https://docs-git-new-nav-mixpanel.vercel.app/docs/tracking-methods/choosing-the-right-method`}
                  imgSrc={more}
                  hasMarginLeft={true}
                />
              </div>
              <div>
                <ProjectToken />
              </div>
            </div>
          </SectionWithMethods>
        </div>
      </>
    );
  };

  const ImplementSdkCTA = () => {
    return (
      <>
        <div className={styles.implementSection}>
          <SectionWithMethods headerTitle='I need to start tracking product data'>
            <div>
              <div className={styles.implementBodyTitle}>
                <Typography variant='captionSm' weight='regular'>
                  {`We recommend you use a server-side implementation.`}
                </Typography>
                <Button
                  onClick={function Cu() {
                    window.open(
                      `https://developer.mixpanel.com/reference/client-side-vs-server-side-tracking`,
                      `_blank`,
                    );
                  }}
                  variant='inline'
                  size='small'
                  includeArrow={false}
                  className={styles.implementBodyButton}
                >
                  Learn more
                </Button>
              </div>
              <div className={styles.implementMethods}>
                <div className={styles.implementMethodRow}>
                  <ImplementMethod
                    title={`JavaScript`}
                    url={`https://developer.mixpanel.com/docs/javascript-quickstart`}
                    imgSrc={js}
                    highlight={`MostPopular`}
                  />
                  <ImplementMethod
                    title={`Android`}
                    url={`https://developer.mixpanel.com/docs/android-quickstart`}
                    imgSrc={android}
                    hasMarginLeft={true}
                  />
                  <ImplementMethod
                    title={`iOS Swift`}
                    url={`https://developer.mixpanel.com/docs/ios-swift-quickstart`}
                    imgSrc={ios}
                    hasMarginLeft={true}
                  />
                </div>
                <div className={styles.implementMethodRow}>
                  <ImplementMethod
                    title={`Objective-C`}
                    url={`https://developer.mixpanel.com/docs/ios-objective-c-quickstart`}
                    imgSrc={ios}
                  />
                  <ImplementMethod
                    title={`More Options`}
                    url={`https://docs-git-new-nav-mixpanel.vercel.app/docs/tracking-methods/choosing-the-right-method`}
                    imgSrc={more}
                    hasMarginLeft={true}
                  />
                </div>
                <div>
                  <ProjectToken />
                </div>
              </div>
            </div>
          </SectionWithMethods>
        </div>
      </>
    );
  };

  const EventListener = ({isIntegrated}) => {
    return (
      <>
        <div className={styles.eventListenerContainer}>
          <div className={styles.eventListenerTitle}>
            <div className={styles.eventListenerImage}>
              {isIntegrated === true ? (
                <img src={eventDetected} />
              ) : (
                <img src={eventListenerGif} width='50' height='9' />
              )}
            </div>
            <Typography variant='bodySm' weight='regular'>
              {isIntegrated === true ? `Events detected!` : `Listening for events...`}
            </Typography>
          </div>
          <div className={styles.eventListenerDescription}>
            {isIntegrated === true ? (
              <div className={styles.eventCheckMarkImage}>
                <img src={checkmark} />
              </div>
            ) : (
              ``
            )}
            <Typography variant='bodySm' weight='regular'>
              {isIntegrated === true ? `Last checked - Just now` : `No events detected - Just now`}
            </Typography>
          </div>
        </div>
      </>
    );
  };

  const InviteFooter = () => {
    const {setVisibility} = React.useContext(VisibilityContext);

    return (
      <>
        {`Need help from your team? `}
        <a
          onClick={() => {
            const chromeHeader = document.querySelector<ChromeHeader>(`mp-chrome-header`);
            chromeHeader.helpers.openInviteUserModal(() => setVisibility(true));
            setVisibility(false);
          }}
        >{`Invite a teammate`}</a>
        {` to complete this step — you have unlimited seats.`}
      </>
    );
  };

  const SectionButton = ({title, onClick}) => {
    return (
      <>
        <div className={styles.implementFooterButton}>
          <Button
            onClick={() => {
              onClick();
            }}
          >
            {title}
          </Button>
        </div>
      </>
    );
  };

  const ImplementCTA = () => {
    const {isIntegrated, setIsIntegrated} = React.useContext(SetupGuideContext);
    const [integrationCheckIntervalID, setIntegrationCheckIntervalID] = React.useState(null);

    type CheckIntegrationResult = {is_integrated: boolean};

    const getProjectIntegrationStatus = async () => {
      try {
        const res: CheckIntegrationResult = await apiClient.get(appApiUrl(`check-integration`, {projectId}));
        setIsIntegrated(res.is_integrated);
        if (res.is_integrated) {
          setIntegrationCheckIntervalID((currentId) => {
            clearInterval(currentId);
            return null;
          });
        }
      } catch (error) {
        console.error(error);
      }
    };

    useEffect(() => {
      const setupIntegrationCheckPolling = () => {
        if (!isIntegrated && !integrationCheckIntervalID) {
          const intervalID = window.setInterval(getProjectIntegrationStatus, 10000);
          setIntegrationCheckIntervalID(intervalID);
        }
      };
      getProjectIntegrationStatus().then(setupIntegrationCheckPolling);

      return () => {
        if (integrationCheckIntervalID) {
          clearInterval(integrationCheckIntervalID);
        }
      };
    }, []);

    return (
      <>
        {
          <div>
            <ImplementCdpCTA />
            <ImplementSdkCTA />
            <EventListener isIntegrated={isIntegrated} />
            <InviteFooter />
            <SectionButton
              title={`Continue`}
              onClick={() => {
                setStep(Step.Explore);
              }}
            />
          </div>
        }
      </>
    );
  };

  return (
    <>
      <Header title={`Send your data`} step={`step 2`} duration={15} imgSrc={sendData} />
      <Content>
        <EventBanner
          events={setupGuideInfo ? setupGuideInfo.events : []}
          lastEditedUser={setupGuideInfo ? setupGuideInfo.author : ``}
          lastEditedDate={setupGuideInfo ? moment(setupGuideInfo.modified).format(`MMM D, YYYY`) : ``}
        />
        <div className={styles.implementHeader}>
          <Typography className={styles.implementHeaderText} variant='bodySm'>
            {`You’re ready to start sending data. Choose the method below that best represents you:`}
          </Typography>
        </div>
        <ImplementCTA />
      </Content>
    </>
  );
};
